import React, { useState, useEffect, useCallback } from 'react';
import CritiqueCard from './CritiqueCard';
import NarrativeCard from './NarrativeCard';
import PackageBuilder from './PackageBuilder';
import ExportPanel from './ExportPanel';
import ExecutiveSummaryPanel from './ExecutiveSummaryPanel';
import {
  getPackage,
  getCritiqueLines,
  getNarratives,
  generatePackage,
  listPackages,
  updatePackage,
  createNarrative,
  getPackageNotes,
} from '../../utils/financialAnalystApi';
import './FinancialAnalyst.css';

const FinancialAnalystResponse = ({ packageId: initialPackageId, generateConfig, locations, currentOrgId }) => {
  const [activeTab, setActiveTab] = useState('critique');
  const [packageData, setPackageData] = useState(null);
  const [critiqueLines, setCritiqueLines] = useState([]);
  const [departmentNotes, setDepartmentNotes] = useState({});
  const [narratives, setNarratives] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [genProgress, setGenProgress] = useState(null);
  const [showExport, setShowExport] = useState(false);
  const [packageId, setPackageId] = useState(initialPackageId);
  const [packageHistory, setPackageHistory] = useState([]);
  const [saving, setSaving] = useState(false);
  const [showNewNarrative, setShowNewNarrative] = useState(false);
  const [newNarrativeTitle, setNewNarrativeTitle] = useState('');
  const [creatingNarrative, setCreatingNarrative] = useState(false);

  // Load package data
  const loadPackage = useCallback(async (id) => {
    if (!id) return;
    setLoading(true);
    try {
      const [pkgResult, linesResult, narrativesResult, notesResult] = await Promise.all([
        getPackage(id),
        getCritiqueLines(id),
        getNarratives(id),
        getPackageNotes(id, { scope: 'all' }).catch(() => ({ notes: [], department_notes: {} })),
      ]);
      setPackageData(pkgResult.package || pkgResult);
      setCritiqueLines(linesResult.critique_lines || linesResult || []);
      // Merge department notes from both sources
      const deptNotes = { ...(linesResult.department_notes || {}), ...(notesResult.department_notes || {}) };
      setDepartmentNotes(deptNotes);
      setNarratives(narrativesResult.narratives || narrativesResult || []);
    } catch (err) {
      console.error('Failed to load package:', err);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (packageId) loadPackage(packageId);
  }, [packageId, loadPackage]);

  // Auto-generate if config provided
  useEffect(() => {
    if (generateConfig && packageId && !generating) {
      handleGenerate(packageId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleGenerate = async (pkgId) => {
    setGenerating(true);
    setGenProgress({ step: 'starting', message: 'Initializing...' });
    try {
      await generatePackage(pkgId, (event) => {
        if (event.type === 'progress') {
          setGenProgress(event);
        } else if (event.type === 'complete') {
          setGenerating(false);
          setGenProgress(null);
          loadPackage(pkgId);
        }
      });
    } catch (err) {
      console.error('Generation failed:', err);
      setGenerating(false);
      setGenProgress(null);
      loadPackage(pkgId);
    }
  };

  const handlePackageCreated = (pkg) => {
    setPackageId(pkg.id);
    setPackageData(pkg);
    handleGenerate(pkg.id);
  };

  const handleRefresh = () => {
    if (packageId) loadPackage(packageId);
  };

  const handleSubmitForReview = async () => {
    if (!packageId || saving) return;
    setSaving(true);
    try {
      await updatePackage(packageId, { status: 'gm_review' });
      await loadPackage(packageId);
    } catch (err) {
      console.error('Failed to submit for review:', err);
    }
    setSaving(false);
  };

  const handleCreateNarrative = async () => {
    if (!packageId || creatingNarrative || !newNarrativeTitle.trim()) return;
    setCreatingNarrative(true);
    try {
      await createNarrative(packageId, {
        sectionType: 'custom',
        sectionTitle: newNarrativeTitle.trim(),
        content: '',
      });
      setNewNarrativeTitle('');
      setShowNewNarrative(false);
      await loadPackage(packageId);
    } catch (err) {
      console.error('Failed to create narrative:', err);
    }
    setCreatingNarrative(false);
  };

  // Load package history for History tab
  const loadHistory = useCallback(async () => {
    try {
      const result = await listPackages({ locationId: packageData?.locationId, limit: 20 });
      setPackageHistory(result.packages || []);
    } catch (err) {
      console.error('Failed to load history:', err);
    }
  }, [packageData?.locationId]);

  useEffect(() => {
    if (activeTab === 'history' && packageData?.locationId) {
      loadHistory();
    }
  }, [activeTab, packageData?.locationId, loadHistory]);

  // Flag counts
  const flagCount = (critiqueLines || []).filter(l => l.flagSeverity === 'red' || l.flagSeverity === 'yellow').length;

  // Extract executive summary narrative
  const execSummary = narratives.find(n => n.sectionType === 'executive_summary') || null;
  const otherNarratives = narratives.filter(n => n.sectionType !== 'executive_summary');

  // No package yet — show builder
  if (!packageId && !generateConfig) {
    return (
      <PackageBuilder
        locations={locations}
        currentOrgId={currentOrgId}
        onPackageCreated={handlePackageCreated}
      />
    );
  }

  // Generating
  if (generating) {
    return (
      <div className="fa-critique-card">
        <div className="fa-progress-overlay">
          <div className="fa-progress-spinner" />
          <div className="fa-progress-message">{genProgress?.message || 'Generating...'}</div>
          <div className="fa-progress-step">{genProgress?.step || ''}</div>
        </div>
      </div>
    );
  }

  // Loading
  if (loading && !packageData) {
    return (
      <div className="fa-critique-card">
        <div style={{ padding: 20 }}>
          <div className="fa-skeleton" style={{ height: 24, width: '60%', marginBottom: 12 }} />
          <div className="fa-skeleton" style={{ height: 16, width: '40%', marginBottom: 20 }} />
          <div className="fa-skeleton" style={{ height: 200, width: '100%' }} />
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Page Tabs */}
      <div className="fa-page-tabs">
        <button
          className={`fa-page-tab ${activeTab === 'critique' ? 'active' : ''}`}
          onClick={() => setActiveTab('critique')}
        >
          Critique {flagCount > 0 && <span className="fa-tab-badge">{flagCount}</span>}
        </button>
        <button
          className={`fa-page-tab ${activeTab === 'narrative' ? 'active' : ''}`}
          onClick={() => setActiveTab('narrative')}
        >
          Narrative
        </button>
        <button
          className={`fa-page-tab ${activeTab === 'package' ? 'active' : ''}`}
          onClick={() => setActiveTab('package')}
        >
          Package
        </button>
        <button
          className={`fa-page-tab ${activeTab === 'history' ? 'active' : ''}`}
          onClick={() => setActiveTab('history')}
        >
          History
        </button>
      </div>

      {/* Critique Tab — side-by-side layout */}
      {activeTab === 'critique' && (
        <div className="fa-critique-layout">
          <div className="fa-critique-layout-left">
            <CritiqueCard
              packageData={packageData}
              critiqueLines={critiqueLines}
              departmentNotes={departmentNotes}
              packageId={packageId}
              onRefresh={handleRefresh}
              onExport={() => setShowExport(true)}
              onSave={handleSubmitForReview}
              saving={saving}
            />
            {otherNarratives.length > 0 && otherNarratives.map((n) => (
              <NarrativeCard
                key={n.id}
                narrative={n}
                onUpdate={handleRefresh}
                isLocked={packageData?.status === 'locked'}
              />
            ))}
          </div>
          <div className="fa-critique-layout-right">
            <ExecutiveSummaryPanel
              narrative={execSummary}
              packageId={packageId}
              isLocked={packageData?.status === 'locked'}
              departmentNotes={departmentNotes}
              onUpdate={handleRefresh}
            />
          </div>
        </div>
      )}

      {/* Narrative Tab — dedicated narrative view */}
      {activeTab === 'narrative' && (
        <div>
          {narratives.map((n) => (
            <NarrativeCard
              key={n.id}
              narrative={n}
              onUpdate={handleRefresh}
              isLocked={packageData?.status === 'locked'}
            />
          ))}

          {/* Add Narrative */}
          {packageData?.status !== 'locked' && (
            showNewNarrative ? (
              <div className="fa-new-narrative-form">
                <input
                  className="fa-form-input"
                  placeholder="Section title (e.g. Revenue Commentary, Expense Deep-Dive...)"
                  value={newNarrativeTitle}
                  onChange={(e) => setNewNarrativeTitle(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleCreateNarrative()}
                  autoFocus
                />
                <div className="fa-new-narrative-actions">
                  <button
                    className="fa-btn fa-btn-sm fa-btn-primary"
                    onClick={handleCreateNarrative}
                    disabled={creatingNarrative || !newNarrativeTitle.trim()}
                  >
                    {creatingNarrative ? 'Creating...' : 'Create'}
                  </button>
                  <button className="fa-btn fa-btn-sm" onClick={() => { setShowNewNarrative(false); setNewNarrativeTitle(''); }}>
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <button
                className="fa-add-narrative-btn"
                onClick={() => setShowNewNarrative(true)}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                Add Narrative Section
              </button>
            )
          )}

          {narratives.length === 0 && !showNewNarrative && (
            <div className="fa-empty-state">
              No narratives yet. Click "Add Narrative Section" above to start writing, or generate a package for AI narratives.
            </div>
          )}
        </div>
      )}

      {/* Package Tab */}
      {activeTab === 'package' && (
        <div className="fa-package-info">
          <div className="fa-package-info-header">
            <h3>{packageData?.title || 'Financial Package'}</h3>
            <span className={`fa-status-pill ${packageData?.status || 'draft'}`}>
              {packageData?.status?.replace(/_/g, ' ') || 'Draft'}
            </span>
          </div>
          <div className="fa-package-info-grid">
            <div className="fa-package-info-item">
              <span className="fa-package-info-label">Template</span>
              <span className="fa-package-info-value">{packageData?.templateType?.replace(/_/g, ' ') || '—'}</span>
            </div>
            <div className="fa-package-info-item">
              <span className="fa-package-info-label">Period</span>
              <span className="fa-package-info-value">
                {packageData?.periodStart ? new Date(packageData.periodStart).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '—'}
                {' — '}
                {packageData?.periodEnd ? new Date(packageData.periodEnd).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}
              </span>
            </div>
            <div className="fa-package-info-item">
              <span className="fa-package-info-label">Created</span>
              <span className="fa-package-info-value">
                {packageData?.createdAt ? new Date(packageData.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—'}
              </span>
            </div>
            <div className="fa-package-info-item">
              <span className="fa-package-info-label">Author</span>
              <span className="fa-package-info-value">{packageData?.createdBy || '—'}</span>
            </div>
            <div className="fa-package-info-item">
              <span className="fa-package-info-label">Critique Lines</span>
              <span className="fa-package-info-value">{critiqueLines.length}</span>
            </div>
            <div className="fa-package-info-item">
              <span className="fa-package-info-label">Narratives</span>
              <span className="fa-package-info-value">{narratives.length}</span>
            </div>
          </div>
        </div>
      )}

      {/* History Tab */}
      {activeTab === 'history' && (
        <div className="fa-history-list">
          {packageHistory.length === 0 && (
            <div className="fa-empty-state">No previous packages found.</div>
          )}
          {packageHistory.map((pkg) => (
            <div
              key={pkg.id}
              className={`fa-history-item ${pkg.id === packageId ? 'active' : ''}`}
              onClick={() => { setPackageId(pkg.id); setActiveTab('critique'); }}
            >
              <div className="fa-history-item-left">
                <div className="fa-history-item-title">{pkg.title}</div>
                <div className="fa-history-item-meta">
                  {pkg.periodStart && new Date(pkg.periodStart).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                  {' · '}
                  {pkg.templateType?.replace(/_/g, ' ')}
                </div>
              </div>
              <span className={`fa-status-pill ${pkg.status || 'draft'}`}>
                {pkg.status?.replace(/_/g, ' ') || 'Draft'}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Export Panel */}
      {showExport && (
        <ExportPanel
          packageId={packageId}
          onClose={() => setShowExport(false)}
        />
      )}
    </>
  );
};

export default FinancialAnalystResponse;
